import { useMutation, useQuery, useQueryClient } from "react-query";
import { toast } from "react-hot-toast";
import { quietPeriodApi } from "../services/api";
import {
  QuietPeriod,
  QuietPeriodCreate,
  QuietPeriodFilters,
  QuietPeriodUpdate,
} from "../types/quiet_period";

export const useQuietPeriods = (filters: QuietPeriodFilters = {}) => {
  return useQuery(
    ["quiet_periods", filters],
    () => quietPeriodApi.getQuietPeriods(filters),
    {
      keepPreviousData: true,
      staleTime: 5 * 60 * 1000,
    },
  );
};

export const useQuietPeriod = (id: number) => {
  return useQuery(
    ["quiet_period", id],
    () => quietPeriodApi.getQuietPeriod(id),
    {
      enabled: !!id,
      staleTime: 5 * 60 * 1000,
    },
  );
};

export const useCreateQuietPeriod = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (data: QuietPeriodCreate) => quietPeriodApi.createQuietPeriod(data),
    {
      onSuccess: (created: QuietPeriod) => {
        queryClient.invalidateQueries(["quiet_periods"]);
        toast.success(`Dynamic date "${created.name}" created successfully`);
      },
      onError: (error: any) => {
        const message =
          error.response?.data?.detail || "Failed to create dynamic date";
        toast.error(message);
      },
    },
  );
};

export const useUpdateQuietPeriod = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, data }: { id: number; data: QuietPeriodUpdate }) =>
      quietPeriodApi.updateQuietPeriod(id, data),
    {
      onSuccess: (updated: QuietPeriod) => {
        queryClient.setQueryData(["quiet_period", updated.id], updated);
        queryClient.invalidateQueries(["quiet_periods"]);
        toast.success(`Dynamic date "${updated.name}" updated successfully`);
      },
      onError: (error: any) => {
        const message =
          error.response?.data?.detail || "Failed to update dynamic date";
        toast.error(message);
      },
    },
  );
};

export const useDeleteQuietPeriod = () => {
  const queryClient = useQueryClient();
  return useMutation((id: number) => quietPeriodApi.deleteQuietPeriod(id), {
    onSuccess: (_, deletedId) => {
      queryClient.removeQueries(["quiet_period", deletedId]);
      queryClient.invalidateQueries(["quiet_periods"]);
      toast.success("Dynamic date deleted successfully");
    },
    onError: (error: any) => {
      const message =
        error.response?.data?.detail || "Failed to delete dynamic date";
      toast.error(message);
    },
  });
};
